import { Injectable } from '@angular/core'
import {
  AppointmentModel,
  EstimateCharge,
  EstimateModifySnapshotVisit,
  EstimateVisitOutput,
} from '@mmx/shared'

export interface ModifyDiff {
  new: number[]
  renumbered: Map<number, number>
  removed: Array<{ visitNumber: number; appointmentId: string | null }>
}

@Injectable({ providedIn: 'root' })
export class EstimateBalanceCalculationService {
  groupChargesByVisit(charges: EstimateCharge[], visitCount: number): Map<number, EstimateCharge[]> {
    const map = new Map<number, EstimateCharge[]>()
    for (const charge of charges) {
      for (const visitNumber of charge.visitNumbers ?? []) {
        if (visitNumber < 1 || visitNumber > visitCount) {
          continue
        }
        const bucket = map.get(visitNumber) ?? []
        bucket.push(charge)
        map.set(visitNumber, bucket)
      }
    }
    return map
  }

  perVisitSubtotal(charges: EstimateCharge[]): number {
    let total = 0
    for (const charge of charges) {
      total += (charge.quantity ?? 0) * (charge.unitCharge ?? 0)
    }
    return total
  }

  grandTotal(visitsToCharges: Map<number, EstimateCharge[]>): number {
    let total = 0
    for (const charges of visitsToCharges.values()) {
      total += this.perVisitSubtotal(charges)
    }
    return total
  }

  // Delegates to the AppointmentModel accessor so per-appointment CPT extraction
  // has a single definition across the estimate-balance services. See PIA-2299 MN14.
  appointmentCptCodes(appointment: AppointmentModel): string[] {
    return appointment.getCptCodes?.() ?? []
  }

  estimatorCptCodesForVisit(charges: EstimateCharge[], visitNumber: number): string[] {
    const codes = new Set<string>()
    for (const charge of charges) {
      if ((charge.visitNumbers ?? []).includes(visitNumber) && charge.cptCode) {
        codes.add(charge.cptCode)
      }
    }
    return Array.from(codes)
  }

  // Mirrors the backend helper in apps/api/src/utils/estimate/course-of-care-visits.ts.
  // N = max(charges.visitNumbers across all charges); 0 when no charge has any.
  deriveUnscheduledVisitCount(charges: EstimateCharge[]): number {
    let max = 0
    for (const charge of charges) {
      for (const n of charge.visitNumbers ?? []) {
        if (n > max) {
          max = n
        }
      }
    }
    return max
  }

  hasUnscheduledPositiveVisitNumbers(charges: EstimateCharge[]): boolean {
    for (const charge of charges) {
      for (const n of charge.visitNumbers ?? []) {
        if (n > 0) {
          return true
        }
      }
    }
    return false
  }

  // Pure diff: snapshot visits (pre-modify) vs new output visits (post-Calculate).
  //
  // Identity is appointmentId-first when present, then visitNumber for pure
  // unscheduled rows. A new visit whose appointmentId is in the snapshot at a
  // different position is renumbered (from = snapshot visit number). A snapshot
  // visit whose appointmentId is no longer in newVisits is removed. Pure
  // unscheduled visit-number swaps appear as new+removed (documented limitation
  // — Resolved decision §13).
  computeModifyDiff(
    snapshotVisits: EstimateModifySnapshotVisit[],
    newVisits: EstimateVisitOutput[],
  ): ModifyDiff {
    const snapAppointmentToNumber = new Map<string, number>()
    const snapUnscheduledVisitNumbers = new Set<number>()
    for (const v of snapshotVisits) {
      if (v.appointmentId != null) {
        snapAppointmentToNumber.set(v.appointmentId, v.visitNumber)
      } else {
        snapUnscheduledVisitNumbers.add(v.visitNumber)
      }
    }

    const newAppointments = new Set<string>()
    const newUnscheduledVisitNumbers = new Set<number>()
    for (const v of newVisits) {
      if (v.appointmentId != null) {
        newAppointments.add(v.appointmentId)
      } else {
        newUnscheduledVisitNumbers.add(v.visitNumber)
      }
    }

    const result: ModifyDiff = { new: [], renumbered: new Map(), removed: [] }

    for (const v of newVisits) {
      if (v.appointmentId != null) {
        const prior = snapAppointmentToNumber.get(v.appointmentId)
        if (prior == null) {
          result.new.push(v.visitNumber)
        } else if (prior !== v.visitNumber) {
          result.renumbered.set(v.visitNumber, prior)
        }
      } else {
        if (!snapUnscheduledVisitNumbers.has(v.visitNumber)) {
          result.new.push(v.visitNumber)
        }
      }
    }

    for (const v of snapshotVisits) {
      if (v.appointmentId != null) {
        if (!newAppointments.has(v.appointmentId)) {
          result.removed.push({ visitNumber: v.visitNumber, appointmentId: v.appointmentId })
        }
      } else if (!newUnscheduledVisitNumbers.has(v.visitNumber)) {
        result.removed.push({ visitNumber: v.visitNumber, appointmentId: null })
      }
    }

    return result
  }
}
